import React, { useState, useEffect } from 'react';
import { logisticsAPI } from '../api';
import { extractErrorMessage } from '../utils/errorHandling';
import LoadingSpinner from './LoadingSpinner';
import ErrorDisplay from './ErrorDisplay';
import type { TrackingInfo, TrackingEvent } from '../types/logistics';

interface ShipmentTrackingProps {
  awb: string;
  onClose?: () => void;
}

const ShipmentTracking: React.FC<ShipmentTrackingProps> = ({ awb, onClose }) => {
  const [tracking, setTracking] = useState<TrackingInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { fetchTracking(); }, [awb]);

  const fetchTracking = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await logisticsAPI.trackShipment(awb);
      setTracking(response.data);
    } catch (err: any) {
      console.error('Error fetching tracking:', err);
      setError(extractErrorMessage(err, 'Failed to fetch tracking information'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => { setRefreshing(true); fetchTracking(); };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-IN', {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const formatLocation = (event: TrackingEvent) => event.location || 'N/A';

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}><LoadingSpinner size="large" message="Loading tracking information..." /></div>;
  }
  if (error) {
    return <ErrorDisplay error={error} onRetry={fetchTracking} onDismiss={() => setError(null)} />;
  }
  if (!tracking) {
    return <div className="tracking-empty">No tracking information available</div>;
  }

  return (
    <div className="tracking-container">
      {/* Header card */}
      <div className="tracking-header-card">
        <div className="tracking-header-top">
          <div>
            <div className="tracking-awb-label">AWB Number</div>
            <code className="tracking-awb-code">{tracking.awb}</code>
          </div>
          <button className="logistics-btn logistics-btn-primary" onClick={handleRefresh} disabled={refreshing}>
            {refreshing ? '⟳ Refreshing...' : '🔄 Refresh'}
          </button>
        </div>
        <div className="tracking-meta-grid">
          <div className="tracking-meta-item">
            <span className="tracking-meta-label">Status</span>
            <span className={`logistics-status-badge status-${tracking.currentStatus}`}>
              {tracking.currentStatus.replace('_', ' ')}
            </span>
          </div>
          <div className="tracking-meta-item">
            <span className="tracking-meta-label">Provider</span>
            <span className="tracking-meta-value">{tracking.provider.toUpperCase()}</span>
          </div>
          {tracking.estimatedDelivery && (
            <div className="tracking-meta-item">
              <span className="tracking-meta-label">Est. Delivery</span>
              <span className="tracking-meta-value">{formatDate(tracking.estimatedDelivery)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Timeline */}
      <div>
        <h3 className="tracking-timeline-title">Tracking History</h3>
        {tracking.events && tracking.events.length > 0 ? (
          <div className="tracking-timeline">
            {tracking.events.map((event, index) => (
              <div className="tracking-event" key={index}>
                <div className="tracking-event-dot" />
                <div className="tracking-event-card">
                  <div className="tracking-event-header">
                    <span className="tracking-event-status">{event.status.replace('_', ' ').toUpperCase()}</span>
                    <span className="tracking-event-time">{formatDate(event.timestamp)}</span>
                  </div>
                  {event.remarks && <div className="tracking-event-remarks">{event.remarks}</div>}
                  <div className="tracking-event-location">📍 {formatLocation(event)}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tracking-empty">No tracking events available yet</div>
        )}
      </div>

      {/* Additional info */}
      <div className="tracking-info-card">
        <h4 className="tracking-info-title">Shipment Information</h4>
        <div className="tracking-info-grid">
          <div className="tracking-meta-item">
            <span className="tracking-meta-label">Provider</span>
            <span className="tracking-meta-value">{tracking.provider.toUpperCase()}</span>
          </div>
          <div className="tracking-meta-item">
            <span className="tracking-meta-label">AWB Number</span>
            <span className="tracking-meta-value">{tracking.awb}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentTracking;
